import { useState, type ReactNode, type CSSProperties } from 'react';
import {
  Box,
  Card,
  Group,
  Stack,
  UnstyledButton,
  type MantineSpacing,
} from '@mantine/core';
import { ChevronDown, ChevronRight } from 'react-feather';

// Soft drop shadow for the `elevated` variant (the borderless, raised card
// look). Lives here so ContentCard is a self-contained, generic primitive.
const ELEVATED_SHADOW = '0 1px 1.5px rgba(0, 0, 0, 0.06)';

/**
 * MED-1012 — Shared content-card primitive.
 *
 * Defines the consistent card shape the in-session panels (Session Guide,
 * Session Notes, AI Insights, Participants) build on: a rounded card with two
 * regions — a header area and a content area.
 *
 * Header area: either pass a ready-made `header` node (panels keep their exact
 * markup while standardizing on the shared shell), or use the structured slots
 * — `icon` + `title` on the left and a right-aligned `actions` slot for
 * per-panel controls (collapse control, badges, "Generate More Prompts", …).
 * Per AC2 the slots are defined here; the panel tickets (MED-1013–MED-1017)
 * supply their contents.
 *
 * Collapse/expand is an opt-in structural capability (AC3): the affordance
 * lives in the base; a panel decides whether to use it in its own ticket.
 *
 * MED-1084 — universal use: the visual shell is now configurable so the
 * post-session cards can adopt this same base without changing appearance.
 * `variant` ('bordered' default = the in-session look; 'elevated' = the
 * post-session no-border + soft-shadow look), plus `padding`, `contentGap`,
 * `radius`, and `dividerColor`. All default to the original in-session values,
 * so existing consumers render unchanged.
 */
export interface ContentCardProps {
  /**
   * Ready-made header content. When provided it owns the entire header row and
   * the structured slots below are ignored. Lets panels adopt the shared shell
   * without changing their existing header markup.
   */
  header?: ReactNode;
  /** Structured header: optional leading icon. */
  icon?: ReactNode;
  /** Structured header: title (string or node, so callers keep their styling). */
  title?: ReactNode;
  /** Structured header: right-aligned actions / badge slot. */
  actions?: ReactNode;
  /** Opt-in collapse/expand affordance (AC3). */
  collapsible?: boolean;
  /** Initial collapsed state when `collapsible`. */
  defaultCollapsed?: boolean;
  /**
   * MED-1014 — render the content area full-bleed (no body padding) beneath a
   * divider under the header, for content that should fill the card edge-to-edge
   * (e.g. the Session Notes editor).
   */
  flushContent?: boolean;
  /**
   * MED-1015 — render a full-width divider between the header and the content
   * (unlike `flushContent`, the content keeps its normal padding). Reusable for
   * the AI Insights panel.
   */
  headerDivider?: boolean;
  /**
   * Fill the parent's height (h="100%"). Default true — a single panel fills its
   * column. Set false when stacking several cards in one column (e.g. the
   * Whiteboard's shared/commitment/document cards) so each is content-height.
   */
  fill?: boolean;
  /**
   * MED-1084 — card surface treatment. 'bordered' (default) = the in-session
   * look (hard border, no shadow). 'elevated' = the post-session look (no
   * border, soft shadow).
   */
  variant?: 'bordered' | 'elevated';
  /** MED-1084 — card padding. Default 'md' (in-session); post-session uses 'lg'. */
  padding?: MantineSpacing;
  /**
   * MED-1084 — gap between the header and the content (the inner Stack gap).
   * Default 'sm' (in-session); post-session uses 'md'.
   */
  contentGap?: MantineSpacing;
  /** MED-1084 — corner radius. Default 12. */
  radius?: number | string;
  /**
   * MED-1084 — color of the header/flush dividers. Default the in-session
   * gray-2; post-session cards pass their own (e.g. '#E5E7EB').
   */
  dividerColor?: string;
  /** Content area. */
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
  /**
   * MED-1084 — forward arbitrary data-* attributes (e.g. data-expanded /
   * data-reviewed on the collapsible post-session cards) to the card root.
   */
  [dataAttr: `data-${string}`]: string | undefined;
}

export function ContentCard({
  header,
  icon,
  title,
  actions,
  collapsible = false,
  defaultCollapsed = false,
  flushContent = false,
  headerDivider = false,
  fill = true,
  variant = 'bordered',
  padding = 'md',
  contentGap = 'sm',
  radius = 12,
  dividerColor = 'var(--mantine-color-gray-2)',
  children,
  className,
  style,
  ...rest
}: ContentCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Bleed margin that pulls flush/divider regions out to the card edges,
  // tracking the active padding so it lines up at any padding size (MED-1084).
  const padToken =
    typeof padding === 'number'
      ? `${padding}px`
      : `var(--mantine-spacing-${padding})`;
  const bleed = `calc(-1 * ${padToken})`;

  const headerContent =
    header !== undefined ? (
      header
    ) : (
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          {icon}
          {title}
        </Group>
        {actions}
      </Group>
    );

  return (
    <Card
      withBorder={variant === 'bordered'}
      padding={padding}
      radius={radius}
      h={fill ? '100%' : undefined}
      className={className}
      style={{
        ...(variant === 'elevated' ? { boxShadow: ELEVATED_SHADOW } : {}),
        ...style,
      }}
      {...rest}
    >
      <Stack gap={contentGap} h={fill ? '100%' : undefined}>
        {collapsible ? (
          <Group gap={8} align="center" wrap="nowrap">
            <UnstyledButton
              onClick={() => setCollapsed(c => !c)}
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              aria-expanded={!collapsed}
              data-testid="content-card-collapse-toggle"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'inherit',
              }}
            >
              {collapsed ? (
                <ChevronRight size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </UnstyledButton>
            <Box style={{ flex: 1, minWidth: 0 }}>{headerContent}</Box>
          </Group>
        ) : (
          headerContent
        )}
        {headerDivider && !collapsed && (
          <Box
            aria-hidden="true"
            style={{
              marginInline: bleed,
              borderTop: `1px solid ${dividerColor}`,
            }}
          />
        )}
        {!collapsed &&
          (flushContent ? (
            <Box
              style={{
                marginInline: bleed,
                marginBottom: bleed,
                borderTop: `1px solid ${dividerColor}`,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {children}
            </Box>
          ) : (
            children
          ))}
      </Stack>
    </Card>
  );
}

export default ContentCard;
